/**
 * The small structural surface needed from GPUQueue. Keeping this structural
 * lets the observer's deterministic self-test run without constructing a real
 * WebGPU device.
 */
export type GPUQueueSubmitTarget = object & {
  submit: (...args: any[]) => any;
};

export type GPUQueueSubmitObservation = {
  /** Successful submit ordinal for this queue, starting at zero. */
  sequence: number;
  /** Number of command buffers when the argument was an Array; null otherwise. */
  commandBufferCount: number | null;
  /** Synchronous observer nesting depth at the instant submit returned. */
  scopeDepth: number;
};

/**
 * Exact half-open slice of the queue's successful-submit history. `cursor`
 * values are next-submit ordinals, so a scope records its entry cursor and
 * requests `[entryCursor, exitCursor)` when it unwinds. Detailed records are
 * retained only for the current/most-recent outer scope; successful submits
 * outside scopes remain visible through `cursor` without allocating records.
 */
export type GPUQueueSubmitHistoryWindow = {
  fromCursor: number;
  toCursor: number;
  retainedStartCursor: number;
  currentCursor: number;
  complete: boolean;
  failureReason: string | null;
  observations: readonly GPUQueueSubmitObservation[];
};

export type GPUQueueSubmitListener = (observation: GPUQueueSubmitObservation) => void;

export type GPUQueueSubmitObserver = {
  /** False when the queue cannot be safely given one own submit wrapper. */
  readonly available: boolean;
  /** Stable diagnostic for an unavailable/replaced hook. */
  readonly failureReason: string | null;
  /** Listener exceptions are isolated from GPUQueue.submit and counted here. */
  readonly listenerFailureCount: number;
  /** Next successful-submit ordinal for this queue. */
  readonly cursor: number;
  /** Current synchronous observer nesting depth. */
  readonly scopeDepth: number;
  /** Read one exact bounded history window without consuming it. */
  readHistory(fromCursor: number, toCursor?: number): GPUQueueSubmitHistoryWindow;
  /**
   * Observe successful queue submissions made synchronously by callback.
   * Only the innermost scope receives a submit, which prevents nested physics
   * engines/ledgers sharing one queue from double-counting each other.
   */
  withScope<T>(listener: GPUQueueSubmitListener, callback: () => T): T;
};

type ScopeEntry = {
  listener: GPUQueueSubmitListener;
};

type ObserverState = {
  target: GPUQueueSubmitTarget;
  originalSubmit: (...args: any[]) => any;
  wrappedSubmit: (...args: any[]) => any;
  scopes: ScopeEntry[];
  available: boolean;
  failureReason: string | null;
  sequence: number;
  listenerFailureCount: number;
  historyStartCursor: number;
  historyCount: number;
  historyWriteIndex: number;
  history: Array<GPUQueueSubmitObservation | undefined>;
  facade: GPUQueueSubmitObserver | null;
};

const QUEUE_OBSERVERS = new WeakMap<object, ObserverState>();
/**
 * Large enough for normal frame scopes, bounded so a forgotten scope cannot
 * turn instrumentation into an unbounded allocation. The implementation is a
 * ring: production rendering outside an observer scope advances only the
 * cursor, while a new outer scope reuses the retained slots from the previous
 * scope. No steady render submit allocates a history record and no trim needs
 * an O(capacity) Array.splice.
 */
export const GPU_QUEUE_SUBMIT_HISTORY_CAPACITY = 4096;

function failureMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

function commandBufferCount(commandBuffers: unknown): number | null {
  // Consuming an arbitrary Iterable just to count it would change the value
  // seen by the real WebGPU implementation. Production submit sites use
  // arrays, for which length is exact and side-effect free.
  return Array.isArray(commandBuffers) ? commandBuffers.length : null;
}

function makeUnavailableState(
  target: GPUQueueSubmitTarget,
  originalSubmit: (...args: any[]) => any,
  failureReason: string,
): ObserverState {
  return {
    target,
    originalSubmit,
    wrappedSubmit: originalSubmit,
    scopes: [],
    available: false,
    failureReason,
    sequence: 0,
    listenerFailureCount: 0,
    historyStartCursor: 0,
    historyCount: 0,
    historyWriteIndex: 0,
    history: [],
    facade: null,
  };
}

function resetHistoryForOuterScope(state: ObserverState): void {
  state.historyStartCursor = state.sequence;
  state.historyCount = 0;
  state.historyWriteIndex = 0;
}

function appendHistory(
  state: ObserverState,
  observation: GPUQueueSubmitObservation,
): void {
  state.history[state.historyWriteIndex] = observation;
  state.historyWriteIndex = (
    state.historyWriteIndex + 1
  ) % GPU_QUEUE_SUBMIT_HISTORY_CAPACITY;
  if (state.historyCount < GPU_QUEUE_SUBMIT_HISTORY_CAPACITY) {
    state.historyCount++;
  } else {
    state.historyStartCursor++;
  }
}

function historyFailure(
  state: ObserverState,
  fromCursor: number,
  toCursor: number,
  reason: string,
): GPUQueueSubmitHistoryWindow {
  return {
    fromCursor,
    toCursor,
    retainedStartCursor: state.historyStartCursor,
    currentCursor: state.sequence,
    complete: false,
    failureReason: reason,
    observations: [],
  };
}

function readHistoryWindow(
  state: ObserverState,
  fromCursor: number,
  toCursor: number,
): GPUQueueSubmitHistoryWindow {
  if (!verifyHook(state)) {
    return historyFailure(
      state,
      fromCursor,
      toCursor,
      state.failureReason ?? 'GPUQueue.submit observation is unavailable.',
    );
  }
  if (
    !Number.isSafeInteger(fromCursor)
    || !Number.isSafeInteger(toCursor)
    || fromCursor < 0
    || toCursor < fromCursor
    || toCursor > state.sequence
  ) {
    return historyFailure(
      state,
      fromCursor,
      toCursor,
      `Invalid GPUQueue submit history window [${fromCursor}, ${toCursor}) at cursor ${state.sequence}.`,
    );
  }
  if (fromCursor === toCursor) {
    return {
      fromCursor,
      toCursor,
      retainedStartCursor: state.historyStartCursor,
      currentCursor: state.sequence,
      complete: true,
      failureReason: null,
      observations: [],
    };
  }
  if (fromCursor < state.historyStartCursor) {
    const retainedEndCursor = state.historyStartCursor + state.historyCount;
    return historyFailure(
      state,
      fromCursor,
      toCursor,
      `GPUQueue submit history gap: requested [${fromCursor}, ${toCursor}), `
        + `retained [${state.historyStartCursor}, ${retainedEndCursor}), `
        + `queue cursor ${state.sequence}.`,
    );
  }

  const retainedEndCursor = state.historyStartCursor + state.historyCount;
  if (toCursor > retainedEndCursor) {
    return historyFailure(
      state,
      fromCursor,
      toCursor,
      `GPUQueue submit history gap: requested [${fromCursor}, ${toCursor}), `
        + `retained [${state.historyStartCursor}, ${retainedEndCursor}), `
        + `queue cursor ${state.sequence}.`,
    );
  }

  const expectedLength = toCursor - fromCursor;
  const observations: GPUQueueSubmitObservation[] = [];
  const oldestIndex = state.historyCount === GPU_QUEUE_SUBMIT_HISTORY_CAPACITY
    ? state.historyWriteIndex
    : 0;
  const startOffset = fromCursor - state.historyStartCursor;
  for (let index = 0; index < expectedLength; index++) {
    const ringIndex = (
      oldestIndex + startOffset + index
    ) % GPU_QUEUE_SUBMIT_HISTORY_CAPACITY;
    const entry = state.history[ringIndex];
    if (entry) observations.push({ ...entry });
  }
  const contiguous = observations.length === expectedLength
    && observations.every((entry, index) => entry.sequence === fromCursor + index);
  if (!contiguous) {
    return historyFailure(
      state,
      fromCursor,
      toCursor,
      `GPUQueue submit history is non-contiguous for [${fromCursor}, ${toCursor}).`,
    );
  }
  return {
    fromCursor,
    toCursor,
    retainedStartCursor: state.historyStartCursor,
    currentCursor: state.sequence,
    complete: true,
    failureReason: null,
    observations,
  };
}

function installObserver(target: GPUQueueSubmitTarget): ObserverState {
  let originalSubmit: (...args: any[]) => any;
  try {
    originalSubmit = Reflect.get(target, 'submit') as (...args: any[]) => any;
  } catch (error) {
    const failed = makeUnavailableState(
      target,
      () => undefined,
      `GPUQueue.submit could not be read: ${failureMessage(error)}`,
    );
    QUEUE_OBSERVERS.set(target, failed);
    return failed;
  }
  if (typeof originalSubmit !== 'function') {
    const failed = makeUnavailableState(target, () => undefined, 'GPUQueue.submit is not callable.');
    QUEUE_OBSERVERS.set(target, failed);
    return failed;
  }

  // Install state in the WeakMap before defining the wrapper. This makes the
  // installation single-shot even for unusual host objects with re-entrant
  // defineProperty traps.
  const state: ObserverState = {
    target,
    originalSubmit,
    wrappedSubmit: originalSubmit,
    scopes: [],
    available: false,
    failureReason: null,
    sequence: 0,
    listenerFailureCount: 0,
    historyStartCursor: 0,
    historyCount: 0,
    historyWriteIndex: 0,
    history: [],
    facade: null,
  };
  QUEUE_OBSERVERS.set(target, state);

  const wrappedSubmit = function(this: unknown, commandBuffers: unknown): any {
    // GPUQueue.submit has exactly one WebIDL argument. Function#call preserves
    // its receiver brand without constructing the rest-parameter array that a
    // variadic wrapper would allocate for every presentation submit.
    const result = state.originalSubmit.call(this, commandBuffers);
    if (this === state.target) {
      const sequence = state.sequence++;
      const scopeDepth = state.scopes.length;
      if (scopeDepth > 0) {
        const active = state.scopes[scopeDepth - 1];
        const observation: GPUQueueSubmitObservation = {
          sequence,
          commandBufferCount: commandBufferCount(commandBuffers),
          scopeDepth,
        };
        // The cursor observes every successful queue submit, which is enough
        // for whole-frame idle detection. Rich history is needed only while a
        // physics/diagnostic scope is active, so ordinary presentation submits
        // take the allocation-free cursor-only path.
        appendHistory(state, observation);
        try {
          active.listener({ ...observation });
        } catch {
          // Instrumentation must never change queue submission semantics.
          state.listenerFailureCount++;
        }
      }
    }
    return result;
  };
  state.wrappedSubmit = wrappedSubmit;

  try {
    const ownDescriptor = Object.getOwnPropertyDescriptor(target, 'submit');
    const replacementDescriptor: PropertyDescriptor = ownDescriptor
      ? ('value' in ownDescriptor
        ? { ...ownDescriptor, value: wrappedSubmit }
        : {
          value: wrappedSubmit,
          configurable: ownDescriptor.configurable,
          enumerable: ownDescriptor.enumerable,
          writable: true,
        })
      : {
        value: wrappedSubmit,
        configurable: true,
        enumerable: false,
        writable: true,
      };
    Object.defineProperty(target, 'submit', replacementDescriptor);
    if (Reflect.get(target, 'submit') !== wrappedSubmit) {
      throw new Error('the installed submit wrapper was not retained');
    }
    state.available = true;
  } catch (error) {
    state.available = false;
    state.failureReason = `GPUQueue.submit observation is unavailable: ${failureMessage(error)}`;
  }
  return state;
}

function stateFor(target: GPUQueueSubmitTarget): ObserverState {
  return QUEUE_OBSERVERS.get(target) ?? installObserver(target);
}

function verifyHook(state: ObserverState): boolean {
  if (!state.available) return false;
  try {
    if (Reflect.get(state.target, 'submit') === state.wrappedSubmit) return true;
    state.available = false;
    state.failureReason = 'GPUQueue.submit observation is unavailable: the submit hook was replaced.';
  } catch (error) {
    state.available = false;
    state.failureReason = `GPUQueue.submit observation is unavailable: ${failureMessage(error)}`;
  }
  return false;
}

/**
 * Return the process-wide observer facade for a queue. Every facade for the
 * same queue shares one WeakMap-backed hook and one synchronous scope stack.
 */
export function getGPUQueueSubmitObserver(target: GPUQueueSubmitTarget): GPUQueueSubmitObserver {
  const state = stateFor(target);
  if (state.facade) return state.facade;
  const facade: GPUQueueSubmitObserver = {
    get available(): boolean {
      return verifyHook(state);
    },
    get failureReason(): string | null {
      verifyHook(state);
      return state.failureReason;
    },
    get listenerFailureCount(): number {
      return state.listenerFailureCount;
    },
    get cursor(): number {
      return state.sequence;
    },
    get scopeDepth(): number {
      return state.scopes.length;
    },
    readHistory(fromCursor: number, toCursor = state.sequence): GPUQueueSubmitHistoryWindow {
      return readHistoryWindow(state, fromCursor, toCursor);
    },
    withScope<T>(listener: GPUQueueSubmitListener, callback: () => T): T {
      if (!verifyHook(state)) return callback();
      if (state.scopes.length === 0) resetHistoryForOuterScope(state);
      const entry: ScopeEntry = { listener };
      state.scopes.push(entry);
      try {
        return callback();
      } finally {
        const removed = state.scopes.pop();
        // A synchronous nested scope must always unwind LIFO. Recover without
        // corrupting other scopes if hostile callback code somehow disturbed
        // the stack through a second facade.
        if (removed !== entry) {
          const index = state.scopes.lastIndexOf(entry);
          if (index >= 0) state.scopes.splice(index, 1);
        }
      }
    },
  };
  state.facade = facade;
  return facade;
}

export type GPUQueueSubmitObserverSelfTestResult = {
  ok: boolean;
  checks: number;
  failures: readonly string[];
};

/** Deterministic, GPU-free contract checks for hook and scope semantics. */
export function runGPUQueueSubmitObserverSelfTest(): GPUQueueSubmitObserverSelfTestResult {
  const failures: string[] = [];
  let checks = 0;
  const check = (name: string, condition: boolean): void => {
    checks++;
    if (!condition) failures.push(name);
  };

  const nativeCalls: number[] = [];
  const queue: GPUQueueSubmitTarget = {
    submit(this: unknown, buffers: unknown[]): number {
      if (this !== queue) throw new TypeError('fake GPUQueue brand mismatch');
      nativeCalls.push(buffers.length);
      if (buffers[0] === 'throw') throw new Error('fake submit failure');
      return buffers.length;
    },
  };
  const observerA = getGPUQueueSubmitObserver(queue);
  const installedSubmit = queue.submit;
  const observerB = getGPUQueueSubmitObserver(queue);
  check('observer hook installs', observerA.available && observerB.available);
  check('same queue reuses one observer facade', observerA === observerB);
  check('same queue is hooked once', queue.submit === installedSubmit);

  const seenA: GPUQueueSubmitObservation[] = [];
  const seenB: GPUQueueSubmitObservation[] = [];
  const returned = observerA.withScope((sample) => seenA.push(sample), () => {
    const first = queue.submit(['a', 'b']);
    observerB.withScope((sample) => seenB.push(sample), () => queue.submit(['nested']));
    queue.submit(['tail']);
    return first;
  });
  check('native return and brand are preserved', returned === 2 && nativeCalls.length === 3);
  check('outer scope sees only its own submits', seenA.length === 2);
  check('nested scope sees only innermost submit', seenB.length === 1);
  check(
    'array command-buffer counts are exact',
    seenA[0]?.commandBufferCount === 2
      && seenB[0]?.commandBufferCount === 1
      && seenA[1]?.commandBufferCount === 1,
  );
  check('nested depth is reported', seenB[0]?.scopeDepth === 2);
  const nestedWindow = observerA.readHistory(0, observerA.cursor);
  check(
    'successful submits are retained in one exact cursor window',
    nestedWindow.complete
      && nestedWindow.observations.length === 3
      && nestedWindow.observations[0]?.scopeDepth === 1
      && nestedWindow.observations[1]?.scopeDepth === 2
      && nestedWindow.observations[2]?.scopeDepth === 1,
  );

  const beforeFailedSubmit = seenA.length;
  try {
    observerA.withScope((sample) => seenA.push(sample), () => queue.submit(['throw']));
  } catch {
    // Expected: a failing native submit must not be reported as successful.
  }
  check('failed native submit is not observed', seenA.length === beforeFailedSubmit);
  const beforeOutsideCursor = observerA.cursor;
  queue.submit(['outside']);
  check('scope unwinds after throw', seenA.length === beforeFailedSubmit);
  check('unscoped submit still advances cursor', observerA.cursor === beforeOutsideCursor + 1);
  const unscopedWindow = observerA.readHistory(beforeOutsideCursor, observerA.cursor);
  check(
    'unscoped submit takes cursor-only path',
    !unscopedWindow.complete && unscopedWindow.observations.length === 0,
  );

  let listenerDidNotBreakSubmit = false;
  const listenerScopeStart = observerA.cursor;
  observerA.withScope(() => {
    throw new Error('listener failure');
  }, () => {
    listenerDidNotBreakSubmit = queue.submit(['listener']) === 1;
  });
  check(
    'listener errors cannot break submit',
    listenerDidNotBreakSubmit && observerA.listenerFailureCount === 1,
  );
  const successfulWindow = observerA.readHistory(listenerScopeStart, observerA.cursor);
  check(
    'new outer scope retains only its exact active window',
    successfulWindow.complete
      && successfulWindow.observations.length === 1
      && successfulWindow.observations[0]?.scopeDepth === 1,
  );
  check(
    'outer-scope reset makes earlier cursor range an explicit gap',
    !observerA.readHistory(0, observerA.cursor).complete,
  );

  let detachedCallRejected = false;
  try {
    const detachedSubmit = queue.submit;
    detachedSubmit(['detached']);
  } catch {
    detachedCallRejected = true;
  }
  check('native receiver brand is not hidden by the wrapper', detachedCallRejected);

  const ringQueue: GPUQueueSubmitTarget = { submit(): void {} };
  const ringObserver = getGPUQueueSubmitObserver(ringQueue);
  ringObserver.withScope(
    () => undefined,
    () => {
      for (let i = 0; i < GPU_QUEUE_SUBMIT_HISTORY_CAPACITY + 3; i++) {
        ringQueue.submit([]);
      }
    },
  );
  const ringEnd = ringObserver.cursor;
  const ringStart = ringEnd - GPU_QUEUE_SUBMIT_HISTORY_CAPACITY;
  const ringTail = ringObserver.readHistory(ringStart, ringEnd);
  check(
    'bounded ring retains an exact contiguous tail',
    ringTail.complete
      && ringTail.observations.length === GPU_QUEUE_SUBMIT_HISTORY_CAPACITY
      && ringTail.observations[0]?.sequence === ringStart
      && ringTail.observations[ringTail.observations.length - 1]?.sequence === ringEnd - 1,
  );
  check(
    'bounded ring reports overwritten prefix as a gap',
    !ringObserver.readHistory(0, ringEnd).complete,
  );
  const reusedScopeStart = ringObserver.cursor;
  ringObserver.withScope(() => undefined, () => ringQueue.submit([]));
  const reusedWindow = ringObserver.readHistory(reusedScopeStart, ringObserver.cursor);
  check(
    'next outer scope reuses ring storage with an exact window',
    reusedWindow.complete
      && reusedWindow.observations.length === 1
      && reusedWindow.observations[0]?.sequence === reusedScopeStart,
  );

  const lockedQueue = Object.freeze({
    submit(): void {},
  }) as GPUQueueSubmitTarget;
  const lockedObserver = getGPUQueueSubmitObserver(lockedQueue);
  let lockedCallbackRan = false;
  lockedObserver.withScope(() => undefined, () => {
    lockedCallbackRan = true;
    lockedQueue.submit([]);
  });
  check('unhookable queue reports unavailable', !lockedObserver.available);
  check('unhookable queue exposes a reason', Boolean(lockedObserver.failureReason));
  check('unavailable observer still runs callback', lockedCallbackRan);

  return { ok: failures.length === 0, checks, failures };
}
